import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { logout } from '../../store/authSlice';
import { COLORS } from '../../shared/colors';
import OverflowTooltip from '../../shared/OverflowTooltip';

const UNAVAILABLE = 'Unavailable';

export const getProfileDisplayName = (user = {}) => (
  [user?.first_name, user?.last_name]
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part) => part.trim())
    .join(' ') || UNAVAILABLE
);

export const getProfileInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length || name === UNAVAILABLE) return '?';
  const selectedParts = parts.length === 1 ? parts : [parts[0], parts[parts.length - 1]];
  return selectedParts.map((part) => part.charAt(0).toUpperCase()).join('');
};

const ProfileValue = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: COLORS.text.muted, display: 'block' }}>
      {label}
    </Typography>
    <OverflowTooltip
      title={value}
      tooltipWidth={350}
      placement="top-start"
      sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    />
  </Box>
);

const HeaderProfileMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth?.user);
  const [anchorEl, setAnchorEl] = useState(null);

  const name = useMemo(() => getProfileDisplayName(user), [user]);
  const initials = useMemo(() => getProfileInitials(name), [name]);
  const email = user?.email?.trim?.() || UNAVAILABLE;
  const role = user?.role?.trim?.().toUpperCase() || UNAVAILABLE;
  const open = Boolean(anchorEl);

  const handleToggle = (event) => {
    setAnchorEl((currentAnchor) => (currentAnchor ? null : event.currentTarget));
  };

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
    navigate('/login');
  };

  return (
    <>
      <Tooltip
        title={open ? '' : name}
        arrow
        placement="bottom"
        slotProps={{ tooltip: { className: 'app-tooltip' } }}
      >
        <IconButton
          onClick={handleToggle}
          aria-label="Open user profile menu"
          aria-haspopup="menu"
          aria-expanded={open}
          size="small"
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <Avatar sx={{ width: 34, height: 34, bgcolor: COLORS.brand.accent, fontSize: 14, fontWeight: 700 }}>
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1400 }}>
        <Paper
          role="menu"
          aria-label="User profile"
          elevation={6}
          sx={{ mt: 1, width: 280, maxWidth: 'calc(100vw - 24px)', p: 2, borderRadius: 2 }}
        >
          <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
            <ProfileValue label="Name" value={name} />
            <ProfileValue label="Email" value={email} />
            <ProfileValue label="Role" value={role} />
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Button fullWidth variant="outlined" color="error" onClick={handleLogout} role="menuitem">
            Logout
          </Button>
        </Paper>
      </Popper>
    </>
  );
};

export default HeaderProfileMenu;
